import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Truck, MapPin, Ban } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchConsoleRoutes, type ConsoleRouteRow } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA Logistics console — the field-operations view over the delivery
// engine: driver routes for the day with per-stop status counts (livrées,
// échecs, en cours) and a blocked flag for tournées awaiting an unblock.
// Reads are logistics-scoped by RLS (migrations 131-140); a non-logistics JWT
// simply sees nothing.
// ---------------------------------------------------------------------------

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function LogistiqueConsole() {
  const q = useQuery({ queryKey: ["djayma", "console-routes"], queryFn: () => fetchConsoleRoutes(), retry: false });

  const rows: ConsoleRouteRow[] = q.data ?? [];
  const totalStops = rows.reduce((acc, r) => acc + r.stops, 0);
  const totalCompleted = rows.reduce((acc, r) => acc + r.completed, 0);
  const totalBlocked = rows.reduce((acc, r) => acc + (r.blocked ? 1 : 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Truck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Logistique — tournées</h1>
            <p className="text-sm text-stone-500">
              Tournées des chauffeurs — zones, véhicules, statut des livraisons
            </p>
          </div>
        </div>

        {q.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Tournées non joignables — réservé aux rôles logistique (RLS). Vérifiez les migrations 131-140 et
            l&apos;exposition du schéma <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi icon={<Truck className="h-5 w-5" />} tone="bg-stone-100 text-stone-700"
               label="Tournées" value={rows.length} />
          <Kpi icon={<MapPin className="h-5 w-5" />} tone="bg-amber-100 text-amber-700"
               label="Stops" value={totalStops} />
          <Kpi icon={<MapPin className="h-5 w-5" />} tone="bg-emerald-100 text-emerald-700"
               label="Livrées" value={totalCompleted} />
          <Kpi icon={<Ban className="h-5 w-5" />} tone="bg-rose-100 text-rose-700"
               label="Tournées bloquées" value={totalBlocked} />
        </div>

        {/* Routes table */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Tournées du jour</h2>
          {rows.length === 0 ? (
            <p className="text-sm text-stone-400">Aucune tournée.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Date</th>
                    <th className="py-2 pr-4 font-medium">Zone</th>
                    <th className="py-2 pr-4 font-medium">Chauffeur</th>
                    <th className="py-2 pr-4 font-medium">Véhicule</th>
                    <th className="py-2 pr-4 font-medium text-right">Stops</th>
                    <th className="py-2 pr-4 font-medium text-right">Livrées</th>
                    <th className="py-2 pr-4 font-medium text-right">Échecs</th>
                    <th className="py-2 pr-4 font-medium text-right">En cours</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-4 text-stone-700">{new Date(r.route_date).toLocaleDateString("fr-FR")}</td>
                      <td className="py-2 pr-4 text-stone-700">{r.zone}</td>
                      <td className="py-2 pr-4 text-stone-700">{r.driver}</td>
                      <td className="py-2 pr-4 text-stone-500">{r.vehicle_ref}</td>
                      <td className="py-2 pr-4 text-right text-stone-800">{r.stops}</td>
                      <td className="py-2 pr-4 text-right font-medium text-emerald-600">{r.completed}</td>
                      <td className="py-2 pr-4 text-right font-medium text-rose-600">{r.failed}</td>
                      <td className="py-2 pr-4 text-right text-stone-500">{r.pending}</td>
                      <td className="py-2 pr-4">
                        {r.blocked ? (
                          <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">
                            Bloquée
                          </span>
                        ) : (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
